package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_PRODUCT_TOGGLE
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.product.data.ProductResponse
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON_DISPLAY
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_BUTTON
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_DESCRIPTION
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_LATEST_TEXT
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_1_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_1_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_2_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_2_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_3_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_PREVIEW_3_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.PRODUCTS_TITLE
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.utils.trackDashboardCardClick

/**
 * Builds the preview list from live API data when available, falling back to static placeholders.
 * Each entry is Triple(imageUrl, title, clickUrl). Live items link to the product detail page.
 *
 * @param products Live product list from the dashboard API, or null when still loading.
 * @return List of (imageUrl, title, clickUrl) triples for the 3 preview cards.
 */
private fun buildProductPreviews(products: List<ProductResponse>?): List<Triple<String, String, String>> =
    if (!products.isNullOrEmpty()) {
        products.map { product ->
            val image = product.imagePath ?: ""
            val title = listOfNotNull(product.brand, product.name).joinToString(" ").ifBlank { "Product" }
            val url = "/productdetail?contentId=${product.contentId}"
            Triple(image, title, url)
        }
    } else {
        listOf(
            Triple(PRODUCTS_PREVIEW_1_URL, PRODUCTS_PREVIEW_1_TITLE, ""),
            Triple(PRODUCTS_PREVIEW_2_URL, PRODUCTS_PREVIEW_2_TITLE, ""),
            Triple(PRODUCTS_PREVIEW_3_URL, PRODUCTS_PREVIEW_3_TITLE, ""),
        )
    }

/**
 * ProductSection
 * Dashboard section showcasing Top Tech Products. Desktop: stacked full-width layout with
 * title/button row, description, and 3-per-row image grid. Mobile: horizontally scrollable cards.
 *
 * @param products Live list of the 3 most recent products from the dashboard API.
 *                 Null while loading — falls back to static placeholder cards.
 */
@OptIn(DelicateApi::class)
@Composable
fun ProductSection(products: List<ProductResponse>? = null) {
    val breakpoint = rememberBreakpoint()
    val previews = buildProductPreviews(products)

    when (breakpoint) {
        Breakpoint.ZERO, Breakpoint.SM, Breakpoint.MD -> ProductSectionMobile(previews)
        else -> ProductSectionDesktop(previews)
    }
}

/**
 * ProductSectionDesktop
 * Full-width stacked layout:
 * 1. Title (left) + CTA button (right) in a SpaceBetween row.
 * 2. Description text spanning full width.
 * 3. "Latest Products" label right-aligned.
 * 4. 3-column flex-wrap grid of [ProductPreviewCard]s.
 *
 * @param previews Resolved (imageUrl, title, clickUrl) triples — live data or static fallback.
 */
@Composable
private fun ProductSectionDesktop(previews: List<Triple<String, String, String>>) {
    val navigator = rememberAppNavigator()

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = 80.px),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.width(clamp(min = 1100.px, value = 80.vw, max = 2000.px)),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                TitleText(title = PRODUCTS_TITLE, color = DarkTextColor)
                GradientButton(
                    text = if (DASHBOARD_PRODUCT_TOGGLE) PRODUCTS_BUTTON else COMING_SOON,
                    buttonHeight = 50,
                    disabled = !DASHBOARD_PRODUCT_TOGGLE,
                    onClick = { navigator.goToProductsPage() },
                )
            }

            DescriptionText(
                modifier = Modifier.padding(top = 16.px),
                color = DarkTextColor,
                description = PRODUCTS_DESCRIPTION,
            )

            if (DASHBOARD_PRODUCT_TOGGLE) {
                DescriptionText(
                    description = PRODUCTS_LATEST_TEXT,
                    color = DarkTextColor,
                    modifier = Modifier.padding(top = 34.px, bottom = 16.px),
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier { property("flex-wrap", "wrap") },
                    horizontalArrangement = Arrangement.spacedBy(24.px),
                    verticalAlignment = Alignment.Top,
                ) {
                    previews.forEach { (url, cardTitle, clickUrl) ->
                        ProductPreviewCard(
                            imageUrl = url,
                            title = cardTitle,
                            clickUrl = clickUrl,
                            modifier = Modifier
                                .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                                .margin(bottom = 16.px),
                        )
                    }
                }
            } else {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 100.px, bottom = 50.px),
                    contentAlignment = Alignment.CenterStart
                ) {
                    TitleText(
                        title = COMING_SOON_DISPLAY,
                        color = DarkTextColor,
                        modifier = Modifier.fillMaxWidth().textAlign(TextAlign.Center),
                    )
                }
            }
        }
    }
}

/**
 * ProductPreviewCard
 * Clickable 16:9 product thumbnail with hover scale on the image and hover underline on the title.
 * Opens [clickUrl] in a new tab on click; does nothing when [clickUrl] is blank.
 *
 * @param imageUrl  Path or URL of the preview image.
 * @param title     Title displayed beneath the image.
 * @param clickUrl  URL to open in a new tab on click. Empty string disables the click.
 * @param modifier  Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun ProductPreviewCard(
    imageUrl: String,
    title: String,
    clickUrl: String,
    modifier: Modifier = Modifier,
) {
    var isHovered by remember { mutableStateOf(false) }
    val isClickable = clickUrl.isNotBlank()

    Column(
        modifier = modifier
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                if (isClickable) onClick {
                    trackDashboardCardClick("products", title, clickUrl)
                    window.open(clickUrl, "_blank")
                }
            }
            .styleModifier {
                if (isClickable) property("cursor", "pointer")
            },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier { property("aspect-ratio", "16 / 9") },
        ) {
            Image(
                src = imageUrl,
                description = title,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("transition", "transform 0.3s ease, filter 0.3s ease")
                        property("transform", if (isHovered && isClickable) "scale(1.06)" else "scale(1.0)")
                        property("filter", if (isHovered && isClickable) "brightness(1.1)" else "brightness(1.0)")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        SpanText(
            text = title,
            modifier = Modifier
                .padding(top = 10.px)
                .color(DarkTextColor)
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .fontWeight(FontWeight.SemiBold)
                .styleModifier {
                    property("display", "inline-block")
                    property("transition", "transform 0.2s ease")
                    property("transform", if (isHovered && isClickable) "scale(1.12)" else "scale(1.0)")
                    property("transform-origin", "left center")
                },
        )
    }
}


//----- Product Section Mobile ---------------------------------------------------------------------------------------


/**
 * ProductSectionMobile
 * Stacked layout: description + button on top, horizontally scrollable preview row below.
 *
 * @param previews Resolved (imageUrl, title, clickUrl) triples — live data or static fallback.
 */
@Composable
private fun ProductSectionMobile(previews: List<Triple<String, String, String>>) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 40.px),
    ) {
        val navigator = rememberAppNavigator()

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 60.px, leftRight = 40.px, bottom = 20.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TitleText(
                title = PRODUCTS_TITLE,
                color = DarkTextColor,
                modifier = Modifier.textAlign(TextAlign.Center),
            )
            DescriptionText(
                modifier = Modifier.padding(top = 24.px).textAlign(TextAlign.Center),
                color = DarkTextColor,
                description = PRODUCTS_DESCRIPTION,
            )
        }

        if (DASHBOARD_PRODUCT_TOGGLE) {
            DescriptionText(
                description = PRODUCTS_LATEST_TEXT,
                color = DarkTextColor,
                modifier = Modifier.padding(left = 24.px, bottom = 12.px),
            )

            Row(
                modifier = WallpaperCarouselStyle.toModifier()
                    .fillMaxWidth()
                    .padding(leftRight = 16.px),
                horizontalArrangement = Arrangement.spacedBy(12.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { (url, cardTitle, clickUrl) ->
                    ProductPreviewCard(
                        imageUrl = url,
                        title = cardTitle,
                        clickUrl = clickUrl,
                        modifier = Modifier.styleModifier {
                            property("width", "85vw")
                            property("flex-shrink", "0")
                        },
                    )
                }
            }
        }

        Box(
            modifier = Modifier.fillMaxWidth().padding(top = 32.px, bottom = 8.px),
            contentAlignment = Alignment.Center,
        ) {
            GradientButton(
                text = if (DASHBOARD_PRODUCT_TOGGLE) PRODUCTS_BUTTON else COMING_SOON,
                disabled = !DASHBOARD_PRODUCT_TOGGLE,
                onClick = { navigator.goToProductsPage() },
            )
        }
    }
}
